/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Stack } from '@mui/material';
import Typography from '../../../../../components/Typography';
import { ModalActions } from '../../../../../components/Modal';
import { Button } from '../../../../../components/Button';
import TestIds from '../../../../../testIds';
import { RESTART_ERROR } from '../constants';
import { FC } from 'react';

export interface ReviewTabProps {
  submitting: boolean;
  submitForm: VoidFunction;
  resetForm: VoidFunction;
  errorMessage?: string;
  overtime: boolean;
  hasNonNullishField: boolean;
}

export const ReviewTabFooter: FC<ReviewTabProps> = ({
  submitting,
  submitForm,
  resetForm,
  hasNonNullishField,
  errorMessage,
  overtime,
}) => {
  return (
    <ModalActions showDivider>
      {/* The minHeight is being set to stop the footer from collapsing when the submit button is not present */}
      <Stack
        width='100%'
        minHeight={'48px'}
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={'center'}
      >
        <Stack width='100%'>
          {!hasNonNullishField && errorMessage && (
            <Typography
              marginRight='auto'
              variant='body1'
              color='error500'
              mb={2}
              maxWidth={{ xs: '100%', md: '90%' }}
            >
              {errorMessage}
            </Typography>
          )}

          {hasNonNullishField && (
            <Typography marginRight='auto' variant='body1' color='error500' mb={2} maxWidth={'90%'}>
              Oops! Some information is missing.
            </Typography>
          )}

          {!hasNonNullishField &&
            !errorMessage &&
            //timer greater than 1 hour
            overtime && (
              <Typography variant='body1' color='error700'>
                You've exceeded the time limit of 1 hour to submit this order.
                <br /> Please refresh the page and try again.
              </Typography>
            )}
        </Stack>

        {!submitting && !overtime && errorMessage !== RESTART_ERROR && (
          <Button
            type='submit'
            onSubmit={submitForm}
            onClick={submitForm}
            key='next-step-btn'
            variant='contained'
            data-testid={TestIds.feat.campaign.reviewTab.submitButton}
            sx={(theme) => ({
              height: 'min-content',
              [theme.breakpoints.down('sm')]: {
                width: '100%',
              },
            })}
          >
            Submit
          </Button>
        )}

        {(overtime || errorMessage?.includes(RESTART_ERROR)) && (
          <Button
            onClick={resetForm}
            key='next-step-btn'
            variant='contained'
            data-testid={TestIds.feat.campaign.reviewTab.submitButton}
            sx={{
              height: 'min-content',
              width: { sm: '100%', md: 'auto' },
            }}
          >
            Restart
          </Button>
        )}
      </Stack>
    </ModalActions>
  );
};
